import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import cn from 'classnames'
import { useEffect, useRef, memo } from 'react'
import { useTransition, animated, config } from 'react-spring'
import { useIsomorphicLayoutEffect } from 'react-use'

import styles from './Modal.module.scss'

import CloseIcon from '~icons/close-extrasmall.svg'
import { addEventListener } from '~lib/helpers'
import usePortalRoots from '~lib/hooks/usePortalRoots'
import Overlay, { OverlayProps } from '~lib/ui-kit/Overlay'
import Portal from '~lib/ui-kit/Portal'

export interface ModalRendererProps extends React.HTMLAttributes<HTMLDivElement> {
  opened: boolean
  showCloseButton?: boolean
  size?: 'xs' | 's' | 'm' | 'l'
  overlayVariant?: OverlayProps['variant'] | 'none'
  overlayClassName?: OverlayProps['className']
  onClose?: (ev: React.MouseEvent<HTMLButtonElement>) => void
  onOverlayClick?: (ev: React.MouseEvent<HTMLElement>) => void
  onEscapePress?: () => void
}

function ModalRenderer({
  id,
  opened,
  showCloseButton,
  size,
  overlayVariant = 'dark',
  children,
  className,
  overlayClassName,
  onClose,
  onOverlayClick,
  onEscapePress,
  ...props
}: React.PropsWithChildren<ModalRendererProps>) {
  const modalRef = useRef<HTMLDivElement>(null)
  const { modalRoot } = usePortalRoots()

  const transition = useTransition(opened, {
    reverse: opened,
    from: { transform: 'scale(0.9)', opacity: 0 },
    enter: { transform: 'scale(1)', opacity: 1 },
    leave: { transform: 'scale(0.9)', opacity: 0 },
    config: config.gentle,
  })

  useEffect(() => {
    if (opened && onEscapePress) {
      return addEventListener(window, 'keydown', (ev) => {
        if (ev.code === 'Escape') onEscapePress()
      })
    }
    return undefined
  }, [opened, onEscapePress])

  useIsomorphicLayoutEffect(() => {
    if (opened && modalRef.current) {
      const node = modalRef.current

      disableBodyScroll(node, { reserveScrollBarGap: true })
      return () => enableBodyScroll(node)
    }
    return undefined
  }, [opened])

  return (
    <Portal root={modalRoot}>
      {transition((style, mounted) => {
        return (
          mounted && (
            <section role="dialog" className={styles.root} {...props}>
              {overlayVariant !== 'none' && (
                <Overlay
                  position="absolute"
                  zIndex={10}
                  show={opened}
                  variant={overlayVariant}
                  className={overlayClassName}
                  onClick={onOverlayClick}
                />
              )}

              <div className={cn(styles.modal, styles[size])}>
                <animated.div ref={modalRef} style={style} className={className}>
                  {showCloseButton && (
                    // TODO - remove close button from Modal because when modal content is lazy loaded the button is displayed
                    <button onClick={onClose} className={styles.close}>
                      <CloseIcon />
                    </button>
                  )}
                  {children}
                </animated.div>
              </div>
            </section>
          )
        )
      })}
    </Portal>
  )
}

export default memo(ModalRenderer)
